import { useEffect, useRef, useState } from 'react';
import { DEEP_LINKABLE_MODALS } from '@PROJECT_ROOT/assets/responsive-frontend/js/constants/modalIds';

const getHashStack = () => window.location.hash.slice(1).split('/').filter(Boolean);

const replaceHashStack = (stack) => {
    const url = new URL(window.location.href);
    url.hash = stack.join('/');
    window.history.replaceState(null, '', url);
};

/**
 * Funkcionira kao običan useState, ali koristi manipulaciju URL hashom kako bi omogućio da se modal zatvara na "back" tipku.
 * Hash predstavlja stack modala odvojen s "/" (npr. #searchMap/wishlistModal), što omogućuje otvaranje modala preko modala.
 *
 * @param {string} modalId id modala koji je potrebno proslijediti i u ModalControl komponentu.
 * @param {number} maxScreenWidth označava maksimalnu širinu (px) viewporta do kojeg se koristi manipulacija URL hashom, iznad toga se koristi običan state.
 */
function useModalState(modalId, maxScreenWidth = 0) {
    const [isOpen, setIsOpen] = useState(false);
    const isOpenRef = useRef(false);
    isOpenRef.current = isOpen;
    const isUsingModalState = useRef(false);
    const isOpenedAsDeepLink = useRef(false);

    useEffect(() => {
        window.history.scrollRestoration = 'manual';
        isUsingModalState.current = window.innerWidth <= maxScreenWidth;

        const initialStack = getHashStack();
        const index = initialStack.indexOf(modalId);
        const isInHash = index !== -1;
        const isModalDeepLinkable = DEEP_LINKABLE_MODALS.has(modalId);
        const initialIsOpen = isModalDeepLinkable && isInHash;
        setIsOpen(initialIsOpen);
        if (!isModalDeepLinkable && isInHash) {
            // Makni svoj segment i sve iznad njega, da child modali ne ostanu bez parenta.
            replaceHashStack(initialStack.slice(0, index));
        }
        if (initialIsOpen) {
            isOpenedAsDeepLink.current = true;
        }

        const handleHashChange = () => {
            if (maxScreenWidth && window.innerWidth > maxScreenWidth) return;
            const stack = getHashStack();
            const isInStack = stack.includes(modalId);
            setIsOpen(isInStack);
            // Deep link flag vrijedi dok god je modal kontinuirano otvoren.
            if (!isInStack) {
                isOpenedAsDeepLink.current = false;
            }
        };

        const handleResize = () => {
            const newIsUsingModalState = window.innerWidth <= maxScreenWidth;
            if (!isOpenRef.current || isUsingModalState.current === newIsUsingModalState) return;
            const stack = getHashStack();
            if (stack.includes(modalId)) {
                // mobile -> desktop: makni iz hasha
                replaceHashStack(stack.filter((id) => id !== modalId));
            } else {
                // desktop -> mobile: dodaj na vrh stacka
                window.location.hash = [...stack, modalId].join('/');
            }
            isUsingModalState.current = newIsUsingModalState;
        };

        window.addEventListener('hashchange', handleHashChange);
        if (maxScreenWidth) {
            window.addEventListener('resize', handleResize);
        }
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
            window.removeEventListener('resize', handleResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setIsOpenCustom = (newIsOpen) => {
        if (maxScreenWidth && window.innerWidth > maxScreenWidth) {
            setIsOpen(newIsOpen);
            return;
        }
        const stack = getHashStack();
        if (newIsOpen) {
            if (!stack.includes(modalId)) {
                window.location.hash = [...stack, modalId].join('/');
            }
            return;
        }
        const index = stack.indexOf(modalId);
        if (index === -1) return;
        const isTopOfStack = index === stack.length - 1;
        if (isTopOfStack && !isOpenedAsDeepLink.current) {
            window.history.back();
            return;
        }
        // Deep link ili zatvaranje modala koji nije na vrhu: makni ga i sve iznad njega.
        // replaceState ne okida hashchange, pa ga ručno dispatchamo da se svi modali sinkroniziraju.
        replaceHashStack(stack.slice(0, index));
        window.dispatchEvent(new HashChangeEvent('hashchange'));
    };

    return [isOpen, setIsOpenCustom];
}

export default useModalState;
